<template>
  <section class="_pts _pb0">
    <div container>
      <form @submit.prevent="search" class="against-grey-bg _mbs">
        <h4 class="_mbs _mt0">Member Search Context:</h4>
        <div grid>
          <div column="6">
            <div class="form-group">
              <label for="lastName">Last Name:</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value=""
                v-model="lastName"
              />
            </div>
          </div>
          <div column="6">
            <div class="form-group">
              <label for="firstName">First Name:</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value=""
                v-model="firstName"
              />
            </div>
          </div>
        </div>

        <div grid>
          <div column="6">
            <div class="form-group">
              <label for="memberId">Member ID:</label>
              <input
                type="text"
                id="memberId"
                name="memberId"
                value=""
                v-model="memberId"
              />
            </div>
          </div>
          <div column="6">
            <app-input
              v-model="ssn"
              type="password"
              valueKey="ssn"
              label="SSN:"
            />
          </div>
        </div>

        <div grid>
          <div column="6">
            <div class="field-group">
              <app-date-input
                ref="dateInput"
                label="DOB"
                name="dob"
                v-model="dob"
              />
            </div>
          </div>
          <div column="6">
            <div class="field-group">
              <app-select
                label="Status"
                name="memberStatus"
                :options="memberStatusOptions"
                v-model="memberStatus"
              ></app-select>
            </div>
          </div>
        </div>

        <div class="_text-center">
          <async-button
            type="submit"
            class="_mtxs --sm"
            :executing="submitting"
          >
            <template #buttonContent>
              <span>Search</span>
            </template>
          </async-button>
        </div>
      </form>
    </div>

    <collapsible-section
      :collapsible="false"
      :collapsed="false"
      class="_p0"
      style="min-height: 22rem;"
    >
      <template #headerText>
        <p class="color-leaf">Member Search Results</p>
      </template>
      <template #headerActions v-if="searchResults">
        <column-picker
          :headers="searchHeaders"
          @updateHeaders="updateHeaders"
        />
      </template>
      <template #collapsibleSection>
        <async-loader v-if="!loaded" />
        <no-results-message
          v-else-if="loaded && !searchResults && !userMessage"
        />
        <div container v-else>
          <v-alert :value="subgroupSecurityViolation" class="_mbs" type="error">
            There are members who match your criteria that you do not have
            permission to view.
          </v-alert>

          <advanced-table
            class="_p0"
            title="Member Results:"
            id="member-results"
            :headers="searchHeaders"
            :items="searchResults"
            :routedData="true"
            :no-data-text="userMessage"
          />
        </div>
      </template>
    </collapsible-section>
  </section>
</template>
<script>
import AdvancedTable from '@organisms/AdvancedTable.vue';
import AsyncButton from '@molecules/AsyncButton.vue';
import AsyncLoader from '@molecules/AsyncLoader.vue';
import AppDateInput from '@atoms/DateInput';
import AppInput from '@atoms/Input';
import AppSelect from '@atoms/Select';
import NoResultsMessage from '@molecules/NoResultsMessage';

import RequestHelper from '@mixins/RequestHelper';
import SearchHelper from '@mixins/SearchHelper';
import queryParamable from '@mixins/queryParamable';
import dateHelper from '@mixins/DateHelper';
import { formatDate } from '@utils';
import { useQueryParams } from '@composables';
import { useStore } from '@vue3shims';
import SearchApi from '@api/SearchApi';
import ColumnPicker from '@molecules/ColumnPicker';
import CollapsibleSection from '@sections/CollapsibleSection';
import {
  computed,
  onBeforeMount,
  onUnmounted,
  ref,
  watch
} from '@vue/composition-api';

export default {
  // mixins: [queryParamable, RequestHelper, SearchHelper, dateHelper],
  props: {
    searchFields: Object
  },
  components: {
    CollapsibleSection,
    AdvancedTable,
    AsyncButton,
    AsyncLoader,
    AppDateInput,
    AppSelect,
    AppInput,
    ColumnPicker,
    NoResultsMessage
  },
  setup(props) {
    const dateInput = ref();
    const eagerQueryParams = ref(false);
    const userMessage = ref('');
    const subgroupSecurityViolation = ref(false);
    const searchHeaders = [
      { text: 'Last Name', align: 'left', value: 'lastName' },
      { text: 'First Name', align: 'left', value: 'firstName' },
      { text: "Client's Member ID", value: 'clientHealthId' },
      { text: 'EyeMed Member ID', value: 'eyemedMemberId', visible: false },
      {
        text: 'Group',
        value: item => `${item.groupName} - ${item.groupId}`,
        truncate: true
      },
      {
        text: 'Subgroup',
        value: item => `${item.subGroupName} - ${item.subGroupId}`,
        visible: false,
        truncate: true
      },
      { text: 'Relationship', value: 'relationshipDescription' },
      { text: 'DOB', value: 'dateOfBirth' },
      { text: 'Last 4 SSN', value: 'ssn', visible: false },
      { text: 'Status', value: 'memberStatus' },
      { text: 'View Details', value: 'route' }
    ];
    const loaded = ref(true);
    const members = ref([]);
    const memberStatusOptions = [
      { text: 'All', value: 'all' },
      { text: 'Active', value: 'active' },
      { text: 'Inactive', value: 'inactive' }
    ];
    const submitting = ref(false);
    const whiteListedQueryParams = [
      'groupId',
      'subgroupId',
      'firstName',
      'lastName',
      'dateOfBirth',
      'memberId',
      'ssn',
      'dateOfService',
      'memberStatus'
    ];

    const store = useStore();

    const search = e => {
      if (dateInput.value) {
        dateInput.value.closeAndEmitValue();
      }

      let groupSelected = store.getters['groupSelector/searchSelection'];
      setTimeout(() => {
        loaded.value = false;
        members.value = [];
        const ms = memberSearch.value;
        const query = {
          firstName: ms.firstName,
          lastName: ms.lastName,
          memberStatus: ms.memberStatus,
          memberId: ms.memberId,
          ssn: ms.ssn,
          dateOfBirth: ms.dob,
          parentGroupCk: groupSelected.parentGroupCk,
          groupCk: groupSelected.groupCk,
          subGroupCk: groupSelected.subGroupCk
        };
        // console.log(`query`, query);
        new SearchApi({
          simulateExample:
            'Member Search- Update Member - Member Exists based on member id, first & last name and dob'
        })
          .searchMembers(query)
          .then(response => {
            if (typeof response === 'object' && response.status) {
              userMessage.value = response.message || response.userMessage;
              members.value = [];
            } else {
              members.value = response.matchingMembers;
              subgroupSecurityViolation.value =
                response.subGroupSecurityViolation;
            }
          })
          .catch(ex => {
            if (ex.toString().indexOf('BadRequestError') !== -1) {
              store.commit('SET_ANNOUNCEMENT_WARNING');
              store.commit(
                'SET_ANNOUNCEMENT',
                'Please enter search criteria before submitting.'
              );
            }
            // err.value = true
          })
          .finally(() => {
            loaded.value = true;
          });
      }, 300);
    };

    const updateHeaders = headers => {
      searchHeaders.value = headers;
    };

    const { queryParams, clearQueryParams } = useQueryParams();
    const dob = computed({
      get() {
        return store.getters['universalSearch/memberSearch'].dob;
      },
      set(value) {
        let opts = { field: 'dob', value: value };
        store.commit('universalSearch/UPDATE_MEMBER_SEARCH_FIELD', opts);
      }
    });

    const firstName = computed({
      get() {
        return store.getters['universalSearch/memberSearch'].firstName;
      },
      set(value) {
        let opts = { field: 'firstName', value: value };
        store.commit('universalSearch/UPDATE_MEMBER_SEARCH_FIELD', opts);
      }
    });
    const lastName = computed({
      get() {
        return store.getters['universalSearch/memberSearch'].lastName;
      },
      set(value) {
        let opts = { field: 'lastName', value: value };
        store.commit('universalSearch/UPDATE_MEMBER_SEARCH_FIELD', opts);
      }
    });
    const memberId = computed({
      get() {
        return store.getters['universalSearch/memberSearch'].memberId;
      },
      set(value) {
        let opts = { field: 'memberId', value: value };
        store.commit('universalSearch/UPDATE_MEMBER_SEARCH_FIELD', opts);
      }
    });
    const memberSearch = computed(
      () => store.getters['universalSearch/memberSearch']
    );
    const memberStatus = computed({
      get() {
        return store.getters['universalSearch/memberSearch'].memberStatus;
      },
      set(value) {
        let opts = { field: 'memberStatus', value: value };
        store.commit('universalSearch/UPDATE_MEMBER_SEARCH_FIELD', opts);
      }
    });
    const ssn = computed({
      get() {
        return store.getters['universalSearch/memberSearch'].ssn;
      },
      set(value) {
        let opts = { field: 'ssn', value: value };
        store.commit('universalSearch/UPDATE_MEMBER_SEARCH_FIELD', opts);
      }
    });
    const searchResults = computed(() => {
      if (
        members.value.status == 'NoRecordFound' ||
        members.value.length == 0
      ) {
        return;
      }
      return members.value.map(member => ({
        ...member,
        route: {
          name: 'member-details',
          params: { memberCk: member.memberCk }
        },
        terminationDate: formatDate(member.terminationDate),
        dateOfBirth: formatDate(member.dateOfBirth),
        effectiveDate: formatDate(member.effectiveDate)
      }));
    });

    watch(queryParams, n => {
      updateSearchFromQuery();
      search();
    });

    const updateSearchFromQuery = () => {
      let groupSelected = store.getters['groupSelector/searchSelection'];

      let query = { ...queryParams.value, ...groupSelected };
      whiteListedQueryParams.forEach(qp => {
        if (!query[qp]) {
          query[qp] = '';
        }
      });
      console.log(`query`, query);
      store.commit('universalSearch/UPDATE_MEMBER_SEARCH', query);
    };

    onBeforeMount(() => {
      updateSearchFromQuery();
      search();
    });

    onUnmounted(() => {
      clearQueryParams();
    });

    return {
      dob,
      eagerQueryParams,
      firstName,
      lastName,
      loaded,
      memberId,
      members,
      memberStatus,
      memberStatusOptions,
      search,
      searchHeaders,
      searchResults,
      ssn,
      subgroupSecurityViolation,
      submitting,
      userMessage,
      updateHeaders,
      whiteListedQueryParams
    };
  }
};
</script>
